import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { ArrowLeft, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/EmptyState';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { parseCallDirectionsParam } from '@/pages/qc/utils';
import { analyticsService } from '@/services/api';
import { useStore } from '@/store';
import { cn } from '@/utils';
import { dateToNaiveString, getTodayDateInTimezone, naiveStringToDate } from '@/utils/date';
import { removeOrgPrefix } from '@/utils/routes';
import { parseIdListParam, serializeMultiValueParam } from '@/utils/urlParams';

import { CallDirectionFilter } from './components/CallDirectionFilter';
import type { ChartDownloadFormat } from './components/ChartDownloadMenu';
import { QuestionAgentBreakdownTable } from './components/QuestionAgentBreakdownTable';
import { QuestionEvaluationsTable } from './components/QuestionEvaluationsTable';
import { QuestionGuide } from './components/QuestionGuide';
import { QuestionScoreImpact } from './components/QuestionScoreImpact';
import { useDownloadChart } from './hooks/useDownloadChart';
import { buildQuestionAgentsExportRequest, useQuestionDetail } from './hooks/useQuestionDetail';
import {
  buildQuestionEvaluationsExportParams,
  fetchAllQuestionEvaluations,
  useQuestionEvaluations,
} from './hooks/useQuestionEvaluations';
import { getDefaultDateRange } from './utils/dateWindow';
import {
  buildQuestionAgentsExport,
  buildQuestionEvaluationsExport,
} from './utils/questionDeepDive';
import { buildAnalyticsListPath, resolveAnalyticsOrganizationId } from './utils/resolveOrg';

const DISPLAY_TOP_AGENTS = 10;
const EVAL_PREVIEW_SIZE = 5;
// The evaluations export pages through the endpoint. Cap the total row count so
// a pathological question can't download an unbounded payload, and give each
// request a long timeout because the missed-evaluation query can run tens of
// seconds.
const EVAL_EXPORT_MAX_ROWS = 20000;
const EVAL_EXPORT_TIMEOUT_MS = 120_000;

const categoryBadgeClass = (category?: string): string => {
  switch ((category ?? '').toLowerCase()) {
    case 'compliance':
      return 'bg-critical text-critical-foreground';
    case 'communication':
      return 'bg-positive text-positive-foreground';
    case 'content':
      return 'bg-attention text-attention-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const QuestionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, metricKey: rawMetricKey } = useParams<{ orgId?: string; metricKey?: string }>();
  const metricKey = rawMetricKey ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const { programs, selectedOrganizationId, isLoadingOrgAndPrograms, unauthorizedAccess } =
    useOrgAndPrograms();
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();

  const cleanOrgId = orgId ? removeOrgPrefix(orgId) : null;
  const organizationId = resolveAnalyticsOrganizationId(cleanOrgId, selectedOrganizationId);
  const analyticsListPath = buildAnalyticsListPath(cleanOrgId);
  const analyticsBackParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'org');
    return params.toString();
  }, [searchParams]);
  const analyticsBackHref = `${analyticsListPath}?${analyticsBackParams}`;

  const start = searchParams.get('start') || '';
  const end = searchParams.get('end') || '';
  const defaultDateRange = useMemo(() => getDefaultDateRange(7, timeZone), [timeZone]);
  const effectiveStart = start || defaultDateRange.start;
  const effectiveEnd = end || defaultDateRange.end;
  const programIds = useMemo(
    () => parseIdListParam(searchParams.get('program_ids')),
    [searchParams],
  );
  // Agent-scoped drill-down (from the agent detail question table) carries the
  // agent id here; absent on the org-tab drill-down, which stays org-wide.
  const agentIds = useMemo(() => parseIdListParam(searchParams.get('agent_ids')), [searchParams]);
  const callDirections = useMemo(
    () => parseCallDirectionsParam(searchParams.get('call_directions')),
    [searchParams],
  );

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (!v) params.delete(k);
            else params.set(k, v);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const previousTimeZoneRef = useRef(timeZone);
  const previousDefaultDateRangeRef = useRef(defaultDateRange);

  // Apply last-7-days default when URL has no dates, and update it on timezone
  // changes only if the URL still holds the previous default range.
  useEffect(() => {
    const previousTimeZone = previousTimeZoneRef.current;
    const previousDefaultDateRange = previousDefaultDateRangeRef.current;
    previousTimeZoneRef.current = timeZone;
    previousDefaultDateRangeRef.current = defaultDateRange;

    if (!start || !end) {
      updateSearchParams(defaultDateRange);
      return;
    }

    const isPreviousDefaultDateRange =
      start === previousDefaultDateRange.start && end === previousDefaultDateRange.end;
    if (previousTimeZone !== timeZone && isPreviousDefaultDateRange) {
      updateSearchParams(defaultDateRange);
    }
  }, [defaultDateRange, start, end, timeZone, updateSearchParams]);

  const programOptions = useMemo(
    () =>
      programs
        .filter(p => !!p.id)
        .map(p => ({ label: p.name || p.id || '', value: p.id! }))
        .sort((l, r) => l.label.localeCompare(r.label)),
    [programs],
  );

  const detailQuery = useQuestionDetail({
    organizationId,
    metricKey,
    programIds,
    agentIds,
    callDirections,
    start: effectiveStart,
    end: effectiveEnd,
    timeZone,
    topAgents: DISPLAY_TOP_AGENTS,
  });

  const evaluationsQuery = useQuestionEvaluations({
    organizationId,
    metricKey,
    programIds,
    agentIds,
    callDirections,
    start: effectiveStart,
    end: effectiveEnd,
    timeZone,
    page: 1,
    pageSize: EVAL_PREVIEW_SIZE,
  });

  const summary = detailQuery.data?.summary;
  const questionLabel = summary?.metric_label || metricKey || 'Question';
  const pageLoading = detailQuery.isLoading || isLoadingOrgAndPrograms;

  const download = useDownloadChart();
  const handleAgentsDownload = useCallback(
    async (format: ChartDownloadFormat) => {
      if (!organizationId || !metricKey) return;
      // Fetch the full agent set (not just the displayed top-N) for the export.
      const { params } = buildQuestionAgentsExportRequest({
        organizationId,
        metricKey,
        programIds,
        agentIds,
        callDirections,
        start: effectiveStart,
        end: effectiveEnd,
        timeZone,
      });
      // The fetch is the part useDownloadChart doesn't cover — without this catch
      // a failed request rejects into ChartDownloadMenu's finally-only wrapper and
      // the user just sees the spinner stop with no file and no explanation.
      let rows;
      try {
        const resp = await analyticsService.getQuestionDetail(
          { organization_id: organizationId, metric_key: metricKey },
          { params },
        );
        rows = resp.agent_breakdown ?? [];
      } catch {
        toast.error("Couldn't load the full agent breakdown for download. Please try again.");
        return;
      }
      await download(buildQuestionAgentsExport(rows), format, 'Agent Breakdown');
    },
    [
      organizationId,
      metricKey,
      programIds,
      agentIds,
      callDirections,
      effectiveStart,
      effectiveEnd,
      timeZone,
      download,
    ],
  );

  // The evaluations table previews only the 5 most recent; the download is the
  // "see everything" path. The endpoint caps page_size, so the export pages
  // through it (see fetchAllQuestionEvaluations) up to EVAL_EXPORT_MAX_ROWS.
  // Each page can run for tens of seconds on a high-volume question, so every
  // request gets an extended timeout.
  const handleEvaluationsDownload = useCallback(
    async (format: ChartDownloadFormat) => {
      if (!organizationId || !metricKey) return;
      let rows;
      try {
        rows = await fetchAllQuestionEvaluations({
          organizationId,
          metricKey,
          params: buildQuestionEvaluationsExportParams({
            programIds,
            agentIds,
            callDirections,
            start: effectiveStart,
            end: effectiveEnd,
            timeZone,
          }),
          maxRows: EVAL_EXPORT_MAX_ROWS,
          timeoutMs: EVAL_EXPORT_TIMEOUT_MS,
        });
      } catch {
        toast.error("Couldn't load the full evaluation list for download. Please try again.");
        return;
      }
      // Say so rather than handing over a silently short file.
      if (rows.length >= EVAL_EXPORT_MAX_ROWS) {
        toast.warning(
          `This question has more missed evaluations than a single export can hold — the file contains the ${EVAL_EXPORT_MAX_ROWS.toLocaleString()} most recent. Narrow the date range or programs for the rest.`,
        );
      }
      await download(
        buildQuestionEvaluationsExport(rows, questionLabel),
        format,
        'Evaluations With Lost Points',
      );
    },
    [
      organizationId,
      metricKey,
      programIds,
      agentIds,
      callDirections,
      effectiveStart,
      effectiveEnd,
      timeZone,
      download,
      questionLabel,
    ],
  );

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <EmptyState
        title="Access Denied"
        description="You do not have access to this organization."
        icon={XCircle}
        action={{ label: 'Go to Home', onClick: () => navigate('/') }}
      />
    );
  }

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item to={analyticsBackHref}>Analytics</Breadcrumb.Item>
          <Breadcrumb.Item>Question</Breadcrumb.Item>
          <Breadcrumb.Item>{questionLabel}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="flex h-full flex-col gap-4 px-6 py-6">
        <Link
          to={analyticsBackHref}
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Analytics
        </Link>

        <header className="flex items-center gap-3">
          {pageLoading && !summary ? (
            <Skeleton className="h-7 w-72" />
          ) : (
            <>
              <h1 className="text-xl font-semibold text-foreground">{questionLabel}</h1>
              {summary?.category ? (
                <Badge
                  variant="outline"
                  className={cn('border-transparent', categoryBadgeClass(summary.category))}
                >
                  {summary.category}
                </Badge>
              ) : null}
            </>
          )}
        </header>

        <div className="flex flex-wrap items-center gap-3">
          <SearchableMultiSelect
            options={programOptions}
            selected={programIds}
            onChange={next => updateSearchParams({ program_ids: serializeMultiValueParam(next) })}
            placeholder="All Programs"
            className="w-44 px-3"
            contentClassName="w-auto min-w-[220px]"
            searchPlaceholder="Search programs..."
            emptyMessage="No programs found."
          />
          <CallDirectionFilter
            selected={callDirections}
            onChange={next =>
              updateSearchParams({ call_directions: serializeMultiValueParam(next) })
            }
          />
          <DateRangePicker
            key={`${start}-${end}`}
            initialDateFrom={naiveStringToDate(effectiveStart) ?? getTodayDateInTimezone(timeZone)}
            initialDateTo={naiveStringToDate(effectiveEnd) ?? getTodayDateInTimezone(timeZone)}
            timezone={timeZone}
            showCompare={false}
            onUpdate={({ range }) => {
              if (!range.from) return;
              updateSearchParams({
                start: dateToNaiveString(range.from),
                end: dateToNaiveString(range.to || range.from),
              });
            }}
          />
        </div>

        <QuestionGuide summary={summary} loading={pageLoading} />

        {detailQuery.error ? (
          <EmptyState
            title="Couldn't load this question"
            description={
              detailQuery.error instanceof Error
                ? detailQuery.error.message
                : 'The question detail request failed. Please try again.'
            }
            icon={XCircle}
            action={{ label: 'Retry', onClick: () => detailQuery.refetch() }}
          />
        ) : (
          <>
            <QuestionScoreImpact summary={summary} loading={pageLoading} />

            {organizationId ? (
              <QuestionAgentBreakdownTable
                organizationId={organizationId}
                rows={detailQuery.data?.agent_breakdown ?? []}
                loading={pageLoading}
                totalAgents={detailQuery.data?.total_agents ?? 0}
                onDownload={handleAgentsDownload}
              />
            ) : null}

            {organizationId ? (
              <QuestionEvaluationsTable
                organizationId={organizationId}
                rows={evaluationsQuery.data?.evaluations ?? []}
                loading={evaluationsQuery.isLoading || evaluationsQuery.isFetching}
                timeZone={timeZone}
                totalMissed={summary?.missed_count}
                onDownload={handleEvaluationsDownload}
                error={!!evaluationsQuery.error}
                onRetry={() => evaluationsQuery.refetch()}
              />
            ) : null}
          </>
        )}
      </div>
    </>
  );
};
